import { CommonModule } from '@angular/common'
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatIconModule } from '@angular/material/icon'
import { ToastrService } from '@app/services/toastr.service'
import { AuthService } from '@auth/services/auth.service'
import { environment } from '@environments/environment'
import { CrewMemberInfo, CrewMembers } from '@events/models/crew-member.interface'
import { ServerRepresentedEvent } from '@events/models/event.model'
import { CrewService } from '@events/services/crew.service'
import { NbAutocompleteModule, NbFormFieldModule, NbInputModule, NbPopoverDirective, NbPopoverModule, NbSpinnerModule } from '@nebular/theme'
import { Assignee } from '@workspaces/models/assignee.interface'
import { Subject } from 'rxjs'
import { debounceTime, distinctUntilChanged, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators'
import { InviteItemComponent } from '../invite-item/invite-item.component'
/**
 * Crew invite search component
 */
@Component({
  selector: 'ngx-invite-search',
  templateUrl: './invite-search.component.html',
  styleUrls: ['./invite-search.component.scss'],
  imports: [CommonModule, FormsModule, NbFormFieldModule, NbSpinnerModule, NbPopoverModule, MatIconModule, NbInputModule, NbAutocompleteModule, InviteItemComponent]
})
export class InviteSearchComponent implements OnInit, OnDestroy {
  /** Subject that stops all hot observables */
  private _destroy$ = new Subject()
  /** On destroy implementation */
  ngOnDestroy() {
    this._destroy$.next(null)
    this._destroy$.complete()
  }
  /**
   * Current event
   */
  @Input()
  event: ServerRepresentedEvent
  /**
   * All assigned crew members
   */
  @Input()
  crew: CrewMembers
  /**
   * All unassigned workspace members
   */
  @Input()
  unassignedWorkspaceMembers: Assignee[]

  /**
   * Generates a unique id
   */
  public uniqueId = this.generateUniqueId()
  /**
   * Search term
   */
  public _term = ''
  /**
   * Performing an async operation?
   */
  public processing = false
  /**
   * Crew search match list
   */
  public list: CrewMemberInfo[] = []
  /**
   * Search term subject
   */
  public term$: Subject<string> = new Subject()
  /**
   * Popover reference
   */
  @ViewChild(NbPopoverDirective) popover: NbPopoverDirective
  /**
   * Creates the component and injects it's dependencies
   * @param crewService CrewService
   * @param toastrService ToastrService
   * @param auth AuthService
   */
  constructor(private crewService: CrewService, private toastrService: ToastrService, private auth: AuthService) {}

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.term$
      .pipe(
        tap(() => this.popover.hide()),
        debounceTime(300),
        distinctUntilChanged(),
        filter((term) => /.{2,}/gm.test(term)),
        switchMap((term) =>
          this.crewService.search(term).pipe(
            map((result: CrewMemberInfo[]) => {
              return result.filter((r: CrewMemberInfo) => this.filterExistingCrew(r))
            }),
            takeUntil(this._destroy$)
          )
        ),
        takeUntil(this._destroy$)
      )
      .subscribe((result: CrewMemberInfo[]) => {
        this.list = result
        if (this.list.length) {
          this.popover.show()
        } else {
          this.popover.hide()
        }
      })
  }
  /**
   * Gets the search term
   */
  public get term() {
    return this._term
  }
  /**
   * Sets the search term and updates the subject
   */
  public set term(value) {
    this._term = value
    this.term$.next(this._term)
  }
  /**
   * Filters existing crew members
   * @param newCrew string
   * @returns boolean
   */
  filterExistingCrew(newCrew) {
    return this.crew.filter((c) => c.user.email.toLowerCase() !== newCrew.email.toLowerCase())
  }
  /**
   * Selects a search result term to invite to the event as a crew
   * @param value {email: string}
   */
  public selectTerm(value) {
    this._term = value.email
    this.list = []
    this.invite()
  }
  /**
   * Invites an email as a new crew member for the event
   * @returns void
   */
  public invite() {
    if (!this.term || this.term === '') return
    this.processing = true
    const crew = this.term
    let invite: any = this.unassignedWorkspaceMembers.find((o) => o.user?.email.trim().toLowerCase() === this.term.trim().toLowerCase()) ?? this.term
    if (Object.prototype.hasOwnProperty.call(invite, 'user')) {
      invite = {
        email: invite['user']['email']
      }
    }
    this.crewService
      .invite(invite, this.event.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        () => {
          this.toastrService.success('Crew invited', 'Successfully invited ' + crew + ' to your crew')
          this.term = ''
          this.processing = false
        },
        (err) => {
          if (Array.isArray(err.error)) {
            const errorCode = err.error[0].substring(err.error[0].indexOf('(') + 1, err.error[0].lastIndexOf(')'))
            if (errorCode === 'C002') {
              this.toastrService.success(crew, 'is already assigned to this event')
            } else {
              this.toastrService.error('Unable to invite crew', crew + ' was not invited, check the email and try again')
            }
          } else {
            this.toastrService.error('', err.error['email'])
          }
          this.processing = false
        }
      )
  }
  /**
   * generates a UUID
   * @returns string
   */
  generateUniqueId(): string {
    return Math.random().toString(36).substr(2, 7)
  }
  /**
   * Opens a new tab that automatically logins the user to the crew system
   */
  openCrewSystem() {
    // Crew now performs its own SSO flow (no token handoff via URL).
    window.open(`${environment.crewSystem}`, '_blank')
  }
}
